import {
  ActionIcon,
  Box,
  Group,
  TextInput,
  MultiSelect,
  Badge,
  useMantineTheme,
} from '@mantine/core';
import { IconEdit, IconSearch, IconTrash, IconX } from '@tabler/icons-react';
import { formatDate, formatCurrencyWithType } from '../utils/formatters';
import {
  InventoryRecord,
  InventoryCategoriesRecord,
} from '../types/pocketbase-types';
import { useState, useMemo } from 'react';
import { useMediaQuery, useDebouncedCallback } from '@mantine/hooks';
import ImageCell from './ImageCell';
// TODO: rename to CategoriesCell to align with ImageCell
import CategoryDisplay from './CategoryDisplay';

type ExpandedInventoryRecord = InventoryRecord & {
  expand: { categories: InventoryCategoriesRecord[] };
};

interface InventoryColumnsProps {
  nameQuery: string;
  setNameQuery: (query: string) => void;
  setPage: (page: number) => void;
  priceFilterPopover: React.ReactNode;
  isActiveFilter: boolean;
  isPriceRangeValid: (range: [number | null, number | null]) => boolean;
  localPriceRange: [number | null, number | null];
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  selectedManufacturers: string[];
  setSelectedManufacturers: (manufacturers: string[]) => void;
  selectedCurrencyTypes: {
    selling: string[];
    purchase: string[];
    estimated: string[];
  };
  setSelectedCurrencyTypes: (
    type: 'selling' | 'purchase' | 'estimated',
    currencies: string[]
  ) => void;
  categoriesData: InventoryCategoriesRecord[];
  manufacturersData: string[];
  onEditClick: (item: ExpandedInventoryRecord) => void;
  onDeleteClick: (item: ExpandedInventoryRecord) => void;
  records?: ExpandedInventoryRecord[];
}

export function useInventoryColumns({
  nameQuery,
  setNameQuery,
  setPage,
  selectedCategories,
  setSelectedCategories,
  selectedManufacturers,
  setSelectedManufacturers,
  selectedCurrencyTypes,
  setSelectedCurrencyTypes,
  categoriesData,
  manufacturersData,
  onEditClick,
  onDeleteClick,
  records,
}: InventoryColumnsProps) {
  const [nameSearch, setNameSearch] = useState('');

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setNameQuery(value);
    setPage(1);
  }, 300);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setNameSearch(value);
    // setNameQuery(value);
    debouncedSearch(value);
  };
  const theme = useMantineTheme();
  const isLargeScreen = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`);

  // Calculate totals and check currency consistency
  const {
    totalPurchasePrice,
    totalSellingPrice,
    totalEstimatedSellingPrice,
    allSamePurchaseCurrency,
    allSameSellingCurrency,
    allSameEstimatedCurrency,
    purchaseCurrency,
    sellingCurrency,
    estimatedCurrency,
  } = useMemo(() => {
    let totalPurchase = 0;
    let totalSelling = 0;
    let totalEstimated = 0;
    let currentPurchaseCurrency: string | null = null;
    let currentSellingCurrency: string | null = null;
    let currentEstimatedCurrency: string | null = null;
    let allSamePurchaseCurrency = true;
    let allSameSellingCurrency = true;
    let allSameEstimatedCurrency = true;

    records?.forEach((record) => {
      if (currentPurchaseCurrency === null) {
        currentPurchaseCurrency = record.purchase_price_currency;
      } else if (record.purchase_price_currency !== currentPurchaseCurrency) {
        allSamePurchaseCurrency = false;
      }

      if (currentSellingCurrency === null) {
        currentSellingCurrency = record.selling_price_currency;
      } else if (record.selling_price_currency !== currentSellingCurrency) {
        allSameSellingCurrency = false;
      }

      if (currentEstimatedCurrency === null) {
        currentEstimatedCurrency = record.estimated_selling_price_currency;
      } else if (
        record.estimated_selling_price_currency !== currentEstimatedCurrency
      ) {
        allSameEstimatedCurrency = false;
      }

      totalPurchase += record.purchase_price || 0;
      totalSelling += record.selling_price || 0;
      totalEstimated += record.estimated_selling_price || 0;
    });

    return {
      totalPurchasePrice: totalPurchase,
      totalSellingPrice: totalSelling,
      totalEstimatedSellingPrice: totalEstimated,
      allSamePurchaseCurrency,
      allSameSellingCurrency,
      allSameEstimatedCurrency,
      purchaseCurrency: currentPurchaseCurrency,
      sellingCurrency: currentSellingCurrency,
      estimatedCurrency: currentEstimatedCurrency,
    };
  }, [records]);

  return [
    {
      accessor: 'product_name',
      title: 'פריט',
      sortable: true,
      direction: 'auto',
      cellsStyle: () => ({
        width: '5%',
        maxWidth: '100px',
        // whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
      filter: (
        <TextInput
          // w="150px"
          label="פריט"
          description="חיפוש לפי שם פריט"
          placeholder="חיפוש..."
          leftSection={<IconSearch size={14} />}
          rightSection={
            nameQuery ? (
              <ActionIcon
                size="sm"
                variant="transparent"
                c="dimmed"
                onClick={() => {
                  setNameQuery('');
                  setPage(1);
                }}
              >
                <IconX style={{ width: 14, height: 14 }} stroke={1.5} />
              </ActionIcon>
            ) : null
          }
          value={nameSearch}
          onChange={handleChange}
        />
      ),
      filtering: nameQuery !== '',
    },
    {
      accessor: 'image',
      title: 'תמונה',
      minWidth: '10%',
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) => (
        <ImageCell record={record} />
      ),
    },
    {
      accessor: 'quantity',
      title: 'כמות',
      width: '10%',
      textAlign: 'center',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) => record.quantity || 0,
    },
    {
      accessor: 'categories',
      title: 'קטגוריה',
      width: '30%',
      sortable: true,
      draggable: isLargeScreen,
      // cellStyle: () => ({
      //   width: '10px',
      //   maxWidth: '50px',
      // }),
      render: (record: ExpandedInventoryRecord) => (
        <CategoryDisplay categories={record.expand?.categories || []} />
      ),
      filter: (
        <MultiSelect
          w={300}
          comboboxProps={{ withinPortal: false }}
          label="קטגוריות"
          description="סינון לפי קטגוריה"
          data={categoriesData.map((category) => ({
            value: category.name,
            label: category.name,
          }))}
          value={selectedCategories}
          placeholder="בחר קטגוריות..."
          onChange={(newCategories) => {
            setSelectedCategories(newCategories);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} />}
          clearable
          searchable
          nothingFoundMessage="לא נמצאו קטגוריות"
        />
      ),
      filtering: selectedCategories.length > 0,
    },
    {
      accessor: 'manufacturer',
      title: 'יצרן',
      width: '10%',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) => record.manufacturer || '-',
      filter: (
        <MultiSelect
          w={300}
          comboboxProps={{ withinPortal: false }}
          label="יצרנים"
          description="סינון לפי יצרן"
          data={manufacturersData}
          value={selectedManufacturers}
          placeholder="בחר יצרנים..."
          onChange={(newManufacturers) => {
            setSelectedManufacturers(newManufacturers);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} />}
          clearable
          searchable
        />
      ),
      filtering: selectedManufacturers.length > 0,
    },
    {
      accessor: 'purchase_price',
      title: 'מחיר רכישה',
      titleStyle: {
        textAlign: 'right',
        direction: 'rtl',
      },
      textAlign: 'center',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) =>
        formatCurrencyWithType(
          record.purchase_price || 0,
          record.purchase_price_currency || 'ILS'
        ),
      filter: (
        <MultiSelect
          w={150}
          comboboxProps={{ withinPortal: false }}
          data={[
            { value: 'ILS', label: '₪ שקל' },
            { value: 'USD', label: '$ דולר' },
            { value: 'EUR', label: '€ אירו' },
            { value: 'GBP', label: '£ פאונד' },
          ]}
          value={selectedCurrencyTypes.purchase}
          placeholder="בחר מטבעות..."
          onChange={(values) => {
            setSelectedCurrencyTypes('purchase', values);
            setPage(1);
          }}
          clearable
        />
      ),
      filtering: selectedCurrencyTypes.purchase.length > 0,
      footer: records?.length
        ? allSamePurchaseCurrency
          ? formatCurrencyWithType(totalPurchasePrice, purchaseCurrency || '')
          : 'מטבע לא זהה'
        : '',
    },
    {
      accessor: 'selling_price',
      title: 'מחיר מכירה',
      titleStyle: {
        textAlign: 'right',
        direction: 'rtl',
      },
      textAlign: 'center',
      sortable: true,
      width: 150,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) =>
        formatCurrencyWithType(
          record.selling_price || 0,
          record.selling_price_currency || 'ILS'
        ),
      filter: (
        <MultiSelect
          w={150}
          comboboxProps={{ withinPortal: false }}
          data={[
            { value: 'ILS', label: '₪ שקל' },
            { value: 'USD', label: '$ דולר' },
            { value: 'EUR', label: '€ אירו' },
            { value: 'GBP', label: '£ פאונד' },
          ]}
          value={selectedCurrencyTypes.selling}
          placeholder="בחר מטבעות..."
          onChange={(values) => {
            setSelectedCurrencyTypes('selling', values);
            setPage(1);
          }}
          clearable
        />
      ),
      filtering: selectedCurrencyTypes.selling.length > 0,
      footer: records?.length
        ? allSameSellingCurrency
          ? formatCurrencyWithType(totalSellingPrice, sellingCurrency || '')
          : 'מטבע לא זהה'
        : '',
    },
    {
      accessor: 'estimated_selling_price',
      title: 'מחיר מכירה משוער',
      titleStyle: {
        textAlign: 'center',
        direction: 'rtl',
      },
      textAlign: 'center',
      // width: '10%',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) =>
        formatCurrencyWithType(
          record.estimated_selling_price || 0,
          record.estimated_selling_price_currency || 'ILS'
        ),
      filter: (
        <MultiSelect
          w={150}
          comboboxProps={{ withinPortal: false }}
          data={[
            { value: 'ILS', label: '₪ שקל' },
            { value: 'USD', label: '$ דולר' },
            { value: 'EUR', label: '€ אירו' },
            { value: 'GBP', label: '£ פאונד' },
          ]}
          value={selectedCurrencyTypes.estimated}
          placeholder="בחר מטבעות..."
          onChange={(values) => {
            setSelectedCurrencyTypes('estimated', values);
            setPage(1);
          }}
          clearable
        />
      ),
      filtering: selectedCurrencyTypes.estimated.length > 0,
      footer: records?.length
        ? allSameEstimatedCurrency
          ? formatCurrencyWithType(
              totalEstimatedSellingPrice,
              estimatedCurrency || ''
            )
          : 'מטבע לא זהה'
        : '',
    },
    {
      accessor: 'arrival_date',
      title: 'תאריך הגעה',
      width: '10%',
      textAlign: 'center',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) =>
        formatDate(record.arrival_date || ''),
    },
    {
      accessor: 'catalog_number',
      title: 'מק״ט',
      width: '5%',
      textAlign: 'left',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) => record.catalog_number || '-',
    },
    {
      accessor: 'original_catalog_number',
      title: 'מק״ט מקורי',
      width: '10%',
      textAlign: 'left',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) =>
        record.original_catalog_number || '-',
    },
    {
      accessor: 'model',
      title: 'דגם',
      width: '10%',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) => record.model || '-',
    },
    {
      accessor: 'location',
      title: 'מיקום',
      width: '10%',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) => record.location || '-',
    },
    {
      accessor: 'RFID',
      title: 'RFID',
      width: '10%',
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) => record.RFID || '-',
    },
    {
      accessor: 'comments',
      title: 'הערות',
      width: '20%',
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) => (
        <Box
          style={{
            minWidth: '150px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {record.comments ? (
            <div
              style={{ direction: 'rtl' }}
              // TODO: check if this is a security concern, there is probably a smarter way to render this
              dangerouslySetInnerHTML={{ __html: record.comments }}
            />
          ) : (
            '-'
          )}
        </Box>
      ),
    },
    {
      accessor: 'created',
      title: 'נוצר בתאריך',
      width: '10%',
      textAlign: 'center',
      sortable: true,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) =>
        formatDate(record.created || ''),
    },
    {
      accessor: 'actions',
      title: <Box mr={6}>פעולות</Box>,
      draggable: isLargeScreen,
      render: (record: ExpandedInventoryRecord) => (
        <Group gap={4} justify="center" wrap="nowrap">
          <ActionIcon
            size="sm"
            variant="subtle"
            color="blue"
            onClick={() => onEditClick(record)}
          >
            <IconEdit size={16} />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="red"
            onClick={() => onDeleteClick(record)}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ),
    },
  ];
}
